import { useEffect, useState } from "react";
import { Box, Card, Grid, ListItem, CircularProgress, Snackbar, Alert, TextareaAutosize } from "@mui/material";
import Checkbox from "../shared/components/Checkbox";
import AppLayout from "../layouts/AppLayout";
import ColorPicker from "../components/Branding/ColorPicker";
import PreviewSurvey from "../components/Survey/preview/PreviewSurvey";
import Button from "../components/UserInput/Buttons/Button";
import appApi from "../api";
import _ from "lodash";
import { toast } from "react-toastify";
import isEmail from "validator/lib/isEmail";
import isURL from "validator/lib/isURL";
import ImageUpload from "components/UserInput/ImageUpload";
import Input from "shared/components/Input";
import FontPicker from "components/Branding/FontPicker";

const LINK_PREFIX = "https://";

const Branding = () => {
    const [showSaveBtn, setShowSaveBtn] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [loading, setLoading] = useState(true);
    const [themeColors, setThemeColors] = useState(null);
    const [originalColors, setOriginalColors] = useState(themeColors); // Used to revert changes
    /** @type {[File, React.Dispatch<React.SetStateAction<File>>]} */
    const [mainLogoFile, setMainLogoFile] = useState(null);
    /** @type {[File, React.Dispatch<React.SetStateAction<File>>]} */
    const [appLogoFile, setAppLogoFile] = useState(null);
    /** @type {[File, React.Dispatch<React.SetStateAction<File>>]} */
    const [landingImageFile, setLandingImageFile] = useState(null);
    /** @type {[File, React.Dispatch<React.SetStateAction<File>>]} */
    const [recommendationsImageFile, setRecommendationsImageFile] = useState(null);
    /** @type {[File, React.Dispatch<React.SetStateAction<File>>]} */
    const [advisorImageFile, setAdvisorImageFile] = useState(null);

    /** @type {[File, React.Dispatch<React.SetStateAction<File>>]} */
    const [customBodyFontFile, setCustomBodyFontFile] = useState(null);
    /** @type {[File, React.Dispatch<React.SetStateAction<File>>]} */
    const [customHeaderFontFile, setCustomHeaderFontFile] = useState(null);

    const cancelEdit = () => {
        setShowSaveBtn(false);
        setThemeColors((theme) => ({ ...theme, ...originalColors }));
    };

    //TODO Validate more than email. Email was added to complete https://pmgint.atlassian.net/browse/APPS-3943, didn't want
    //TODO to both increasing the ticket's scope to validate the entire form. Used array for errors so we have a starting point
    //TODO for adding validation to the rest of the fields.
    const validateTheme = () => {
        const contactUsEmail = themeColors.contactUs.email?.trim();

        const errors = [];

        if (contactUsEmail && !isEmail(contactUsEmail)) {
            errors.push("Contact us email address is invalid");
        }

        const nextStepsButtonText = themeColors.nextStepsButton?.text;
        const nextStepsButtonLink = themeColors.nextStepsButton?.link;

        // If one of the next steps button fields is filled, the other must be filled as well
        if ((nextStepsButtonText && !nextStepsButtonLink) || (!nextStepsButtonText && nextStepsButtonLink)) {
            errors.push("Next steps button text and link must both be filled");
        }

        if (nextStepsButtonLink && !isURL(LINK_PREFIX + nextStepsButtonLink, { protocols: ["http", "https"] })) {
            errors.push("Next steps button link is invalid");
        }

        return errors;
    };

    const saveTheme = async () => {
        try {
            const errors = validateTheme();
            if (errors.length > 0) {
                toast.error(
                    errors.length === 1 ? (
                        errors[0]
                    ) : (
                        <ul style={{ marginLeft: "24px" }}>
                            {errors.map((error, index) => (
                                <li key={`save-theme-error-${index}`} style={{ listStyleType: "disc" }}>
                                    {error}
                                </li>
                            ))}
                        </ul>
                    ),
                );
                return;
            }

            const formData = new FormData();
            formData.append("appLogo", appLogoFile);
            formData.append("mainLogo", mainLogoFile);
            formData.append("landingImage", landingImageFile);
            formData.append("recommendationsImage", recommendationsImageFile);
            formData.append("advisorImage", advisorImageFile);
            formData.append("customBodyFont", customBodyFontFile);
            formData.append("customHeaderFont", customHeaderFontFile);

            delete themeColors.mainLogo; // Don't include these in the JSON because it's just a data URL that's used to show the preview
            delete themeColors.landingImage;
            delete themeColors.appLogo;
            delete themeColors.recommendationsImage;
            delete themeColors.advisorImage;

            if (themeColors.customBodyFont.file?.data) {
                delete themeColors.customBodyFont.file.data;
            }
            if (themeColors.customHeaderFont.file?.data) {
                delete themeColors.customHeaderFont.file.data;
            }
            formData.append(
                "theme",
                JSON.stringify({
                    ...themeColors,
                    nextStepsButtonLink: LINK_PREFIX + themeColors.nextStepsButtonLink,
                }),
            );

            const response = await appApi.post("/surveys/theme", formData);

            setThemeColors((theme) => ({ ...theme, ...response.data.theme }));
            setOriginalColors((theme) => ({ ...theme, ...response.data.theme }));
            setShowSnackbar(true);
        } catch (error) {
            if (error.response?.status === 413) {
                // Backend should return a message that states the maximum allowed size, but fallback to a generic message just in case
                toast.error(error.response.data?.message ?? "Image size is too large");
            } else {
                console.log(error);
                toast.error("Internal error occurred saving the survey theme");
            }
        }
    };

    //TODO Error handling:
    //TODO  - Doing this properly isn't worth it because the backend doesn't return 4xx or 5xx codes on failure.
    //TODO  - After fixing the backend, add a try/catch/finally block and put the setLoading(false) call into the
    //TODO    finally block, and move the toast message into the catch block
    const getTheme = async () => {
        try {
            setLoading(true);

            const response = await appApi.get("/surveys/theme");
            let response_theme = {
                ...response.data.theme,
                nextStepsButtonLink: response.data.theme.nextStepsButtonLink?.replace(LINK_PREFIX, ""),
            };

            setThemeColors((theme) => ({ ...theme, ...response_theme }));
            setOriginalColors((theme) => ({ ...theme, ...response_theme }));
        } catch (error) {
            toast.error("Internal error occurred getting the survey theme");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getTheme();
    }, []);

    //detect changes
    useEffect(() => {
        if (_.isEqual(themeColors, originalColors)) {
            setShowSaveBtn(false);
        } else {
            setShowSaveBtn(true);
        }
    }, [themeColors, originalColors]);

    return (
        <AppLayout>
            <div className="mx-[46px] mb-[36px]">
                <Grid container spacing={5}>
                    <Grid item xs={12} sm={12} md={5} xl={4}>
                        <Card
                            variant="outlined"
                            sx={{ padding: 2, px: 4, overflow: "visible" }}
                            className="border-slate-100 rounded-[10px]"
                        >
                            <Box display="flex" justifyContent="space-between" paddingBottom={1}>
                                <div className="text-[#455468] text-[20.7px]">Color Palette</div>
                            </Box>

                            {loading ? (
                                <Box display="flex" justifyContent="center" my={10}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Box>
                                    <div className="grid grid-cols-2">
                                        <ListItem onClick={(event) => console.log(event)} sx={{ pl: 0 }}>
                                            <ColorPicker
                                                title="Primary"
                                                defaultColor={themeColors.primaryColor}
                                                setColor={(val) =>
                                                    setThemeColors((colors) => ({ ...colors, primaryColor: val }))
                                                }
                                            ></ColorPicker>
                                        </ListItem>

                                        <ListItem onClick={(event) => console.log(event)} sx={{ pl: 0 }}>
                                            <ColorPicker
                                                title="Secondary"
                                                defaultColor={themeColors.secondaryColor}
                                                setColor={(val) =>
                                                    setThemeColors((colors) => ({ ...colors, secondaryColor: val }))
                                                }
                                            ></ColorPicker>
                                        </ListItem>
                                        <ListItem onClick={(event) => console.log(event)} sx={{ pl: 0 }}>
                                            <ColorPicker
                                                title="Button background"
                                                defaultColor={themeColors.buttonBackgroundColor}
                                                setColor={(val) =>
                                                    setThemeColors((colors) => ({
                                                        ...colors,
                                                        buttonBackgroundColor: val,
                                                    }))
                                                }
                                            ></ColorPicker>
                                        </ListItem>

                                        <ListItem onClick={(event) => console.log(event)} sx={{ pl: 0 }}>
                                            <ColorPicker
                                                pickerColor="text-slate-500"
                                                title="Button text"
                                                defaultColor={themeColors.buttonTextColor}
                                                setColor={(val) =>
                                                    setThemeColors((colors) => ({ ...colors, buttonTextColor: val }))
                                                }
                                            ></ColorPicker>
                                        </ListItem>
                                    </div>
                                    {showSaveBtn && (
                                        <div className="flex items-center space-x-3">
                                            <Button size="xs" colorTheme="primary" handleOnClick={saveTheme}>
                                                Save
                                            </Button>

                                            <Button size="xs" colorTheme="secondary" handleOnClick={cancelEdit}>
                                                Cancel
                                            </Button>
                                        </div>
                                    )}
                                </Box>
                            )}
                        </Card>

                        <Card
                            variant="outlined"
                            sx={{ padding: 2, px: 4, mt: 4, overflow: "visible" }}
                            className="border-slate-100 rounded-[10px]"
                        >
                            <Box display="flex" justifyContent="space-between">
                                <div className="text-[#455468] text-[20.7px] mb-[13px]">Customization</div>
                            </Box>

                            {loading ? (
                                <Box display="flex" justifyContent="center" my={10}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Box>
                                    <ListItem onClick={(event) => console.log(event)} sx={{ pl: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">
                                                Application Name
                                            </div>
                                            <Input
                                                value={themeColors.appName}
                                                onChange={(value) =>
                                                    setThemeColors((theme) => ({
                                                        ...theme,
                                                        appName: value,
                                                    }))
                                                }
                                                type="text"
                                                placeholder="Application Name"
                                            />
                                        </div>
                                    </ListItem>
                                    <ListItem onClick={(event) => console.log(event)} sx={{ pl: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">
                                                Metric Name
                                            </div>
                                            <Input
                                                value={themeColors.metricName}
                                                onChange={(value) =>
                                                    setThemeColors((theme) => ({
                                                        ...theme,
                                                        metricName: value,
                                                    }))
                                                }
                                                type="text"
                                                placeholder="Metric Name"
                                            />
                                        </div>
                                    </ListItem>
                                    <ListItem sx={{ pl: 0, pr: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">
                                                Application Logo
                                            </div>
                                            <ImageUpload
                                                themeVar={"appLogo"}
                                                setFile={setAppLogoFile}
                                                themeColors={themeColors}
                                                setThemeColors={setThemeColors}
                                                altText="application logo"
                                            />
                                        </div>
                                    </ListItem>
                                    <ListItem sx={{ pl: 0, pr: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">Company Logo</div>
                                            <ImageUpload
                                                themeVar={"mainLogo"}
                                                setFile={setMainLogoFile}
                                                themeColors={themeColors}
                                                setThemeColors={setThemeColors}
                                                altText="logo"
                                            />
                                        </div>
                                    </ListItem>
                                    <ListItem sx={{ pl: 0, pr: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">
                                                Landing Page Image
                                            </div>
                                            <ImageUpload
                                                themeVar={"landingImage"}
                                                setFile={setLandingImageFile}
                                                themeColors={themeColors}
                                                setThemeColors={setThemeColors}
                                                altText="landing image"
                                            />
                                        </div>
                                    </ListItem>
                                    <ListItem sx={{ pl: 0, pr: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">
                                                Result Page Recommendations Image
                                            </div>
                                            <ImageUpload
                                                themeVar={"recommendationsImage"}
                                                setFile={setRecommendationsImageFile}
                                                themeColors={themeColors}
                                                setThemeColors={setThemeColors}
                                                altText="recommendations image"
                                            />
                                        </div>
                                    </ListItem>
                                    <ListItem sx={{ pl: 0, pr: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">
                                                Result Page Advisor Image
                                            </div>
                                            <ImageUpload
                                                themeVar={"advisorImage"}
                                                setFile={setAdvisorImageFile}
                                                themeColors={themeColors}
                                                setThemeColors={setThemeColors}
                                                altText="advisor image"
                                            />
                                        </div>
                                    </ListItem>

                                    <ListItem onClick={(event) => console.log(event)} sx={{ pl: 0, pr: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">Call to action</div>
                                            <TextareaAutosize
                                                value={themeColors.callToAction.description}
                                                onChange={(e) =>
                                                    setThemeColors((theme) => ({
                                                        ...theme,
                                                        callToAction: {
                                                            ...theme.callToAction,
                                                            description: e.target.value,
                                                        },
                                                    }))
                                                }
                                                variant="outlined"
                                                aria-label="empty textarea"
                                                className="banner-text-field"
                                            />
                                        </div>
                                    </ListItem>
                                    <ListItem onClick={(event) => console.log(event)} sx={{ pl: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">
                                                Start button text
                                            </div>
                                            <Input
                                                value={themeColors.callToAction.buttonText}
                                                onChange={(value) =>
                                                    setThemeColors((theme) => ({
                                                        ...theme,
                                                        callToAction: {
                                                            ...theme.callToAction,
                                                            buttonText: value,
                                                        },
                                                    }))
                                                }
                                                type="text"
                                                placeholder="Button text"
                                            />
                                        </div>
                                    </ListItem>
                                    <ListItem onClick={(event) => console.log(event)} sx={{ pl: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">
                                                Next steps button
                                            </div>
                                            <span className="paragraph-6-regular text-[#000000de]">Text</span>
                                            <Input
                                                value={themeColors.nextStepsButton?.text ?? ""}
                                                onChange={(value) =>
                                                    setThemeColors((theme) => ({
                                                        ...theme,
                                                        nextStepsButton: {
                                                            ...theme.nextStepsButton,
                                                            text: value,
                                                        },
                                                    }))
                                                }
                                                type="text"
                                                placeholder="Button text"
                                            />
                                            <span className="paragraph-6-regular text-[#000000de]">Link</span>
                                            <Input
                                                value={themeColors.nextStepsButton?.link ?? ""}
                                                onChange={(value) =>
                                                    setThemeColors((theme) => ({
                                                        ...theme,
                                                        nextStepsButton: {
                                                            ...theme.nextStepsButton,
                                                            link: value,
                                                        },
                                                    }))
                                                }
                                                type="text"
                                                prefix={LINK_PREFIX}
                                                placeholder="URL"
                                            />
                                        </div>
                                    </ListItem>
                                    <ListItem onClick={(event) => console.log(event)} sx={{ pl: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">Contact us</div>
                                            <Input
                                                value={themeColors.contactUs.email}
                                                onChange={(value) =>
                                                    setThemeColors((theme) => ({
                                                        ...theme,
                                                        contactUs: { ...theme.contactUs, email: value },
                                                    }))
                                                }
                                                type="text"
                                                placeholder="Email"
                                            />
                                        </div>
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">Images</div>
                                            <Checkbox
                                                label={
                                                    <span className="paragraph-6-regular text-[#000000de]">
                                                        Hide chevrons
                                                    </span>
                                                }
                                                value={themeColors.hideChevrons}
                                                onChange={(checked) => {
                                                    setThemeColors((theme) => ({
                                                        ...theme,
                                                        hideChevrons: checked,
                                                    }));
                                                }}
                                                small
                                            />
                                        </div>
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">Buttons</div>
                                            <Checkbox
                                                label={
                                                    <span className="paragraph-6-regular text-[#000000de]">
                                                        Rounded Corners
                                                    </span>
                                                }
                                                value={themeColors.roundedButtons}
                                                onChange={(checked) => {
                                                    setThemeColors((theme) => ({
                                                        ...theme,
                                                        roundedButtons: checked,
                                                    }));
                                                }}
                                                small
                                            />
                                        </div>
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <div className="w-full">
                                            <div className="text-[#455468] font-semibold mb-[10px]">Fonts</div>
                                            <FontPicker
                                                themeColors={themeColors}
                                                setThemeColors={setThemeColors}
                                                customFontFile={customBodyFontFile}
                                                setCustomFontFile={setCustomBodyFontFile}
                                                themeVar={"customBodyFont"}
                                                label="Body"
                                            />
                                            <FontPicker
                                                themeColors={themeColors}
                                                setThemeColors={setThemeColors}
                                                customFontFile={customHeaderFontFile}
                                                setCustomFontFile={setCustomHeaderFontFile}
                                                themeVar={"customHeaderFont"}
                                                label="Header"
                                            />
                                        </div>
                                    </ListItem>

                                    {showSaveBtn && (
                                        <div className="flex items-center space-x-3">
                                            <Button size="xs" colorTheme="primary" handleOnClick={saveTheme}>
                                                Save
                                            </Button>

                                            <Button size="xs" colorTheme="secondary" handleOnClick={cancelEdit}>
                                                Cancel
                                            </Button>
                                        </div>
                                    )}
                                </Box>
                            )}
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={12} md={7} xl={8} mb={10}>
                        <Card
                            variant="outlined"
                            sx={{ padding: 0, height: "100%" }}
                            className="border-slate-100 rounded-[10px]"
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <div className="text-[#455468] text-[20.7px] py-[28px] px-[30px]">Preview</div>
                            </Box>
                            {loading ? (
                                <Box display="flex" justifyContent="center" my={10}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <PreviewSurvey themeColors={themeColors}></PreviewSurvey>
                            )}
                        </Card>
                    </Grid>
                </Grid>

                <Snackbar
                    open={showSnackbar}
                    autoHideDuration={5000}
                    onClose={() => setShowSnackbar(false)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Alert variant="filled" severity="success" sx={{ width: "100%" }}>
                        Saved successfully
                    </Alert>
                </Snackbar>
            </div>
        </AppLayout>
    );
};

export default Branding;
